/**
 * Catchball Dashboard
 *
 * Activity-driven layout: what needs attention now, then full loop history.
 * Replaces the previous stacked-tables approach with a focused, progressive UI.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  GitBranch,
  Hand,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  RotateCcw,
  Send,
  CircleDot,
  Scale,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  useCatchballStore,
  usePendingCatchballActions,
  useCatchballThreads,
} from '../../../stores/prioritization/catchballStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useProposalPacketStore } from '../../../stores/prioritization/proposalPacketStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { createTrackWithFeedback } from '../../../lib/tracks/createTrackFlow';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useResponsibilityStore } from '../../../stores/prioritization/responsibilityStore';
import { useCatchballVirtualConfigStore } from '../../../stores/prioritization/catchballVirtualConfigStore';
import { computeLoopAccess, computeLoopVisibility } from '../../../hooks/prioritization/useOrgAwareCatchball';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { useToast } from '../../../components/ui/Toast';
import { getWorkingTierAssignableOrgUnits } from '../../../lib/orgUnitTree';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';
import { anotherRoundBlocker } from '../../../lib/catchball/loopGates';
import { CatchballUndoAction } from '../../../components/prioritization/catchball/CatchballUndoAction';
import { CatchballTossButton } from '../../../components/prioritization/catchball/CatchballTossButton';
import { recallBall, cancelCatchballLoop } from '../../../lib/reversals/reversalsService';
import { ReverseActionButton } from '../../../components/reversals/ReverseActionButton';
import { CatchballStickyHeader } from '../../../components/prioritization/catchball/CatchballStickyHeader';
import { BallStrip } from '../../../components/prioritization/catchball/BallStrip';
import { loopDisplayLabel, useLoopDisplayLabel } from '../../../components/prioritization/catchball/loopDisplayLabel';
import { useCatchballBallStore, useBallsForLoop } from '../../../stores/prioritization/catchballBallStore';
import {
  BALL_STATUS_LABELS,
  PROPOSAL_RETURNED_STATUSES,
  ballDirection,
  ballHasReturnedProposal,
  type CatchballBall,
  isAnchorBall,
} from '../../../types/prioritization/catchballBall';
import type { LoopSynthesis, CatchballLoop } from '../../../types/prioritization/catchballLoop';
import { normalizeStringList } from '../../../types/prioritization/catchballLoop';
import {
  getProposalStatusLabel,
  formatProposalAction,
} from '../../../types/prioritization/catchball';
import type { CatchballDirection } from '../../../types/prioritization/catchball';
import { formatCalendarDate } from '../../../lib/calendarDate';

// Pre-Ball item-level proposal surfaces — superseded by the Ball/Reconciliation
// model. Kept behind this flag (instead of deleted) until the panels' data layer
// is retired with them.
const SHOW_LEGACY_ITEM_PROPOSAL_PANELS = false as boolean;

// ─── Ball status color map ────────────────────────────────────────────────────

const BALL_STATUS_COLORS: Partial<Record<string, string>> = {
  accepted:               'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border-[var(--green)]',
  reconciled:             'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border-[var(--green)]',
  accepted_provisional:   'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]',
  proposal_returned:      'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]',
  under_review:           'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]',
  returned_for_revision:  'text-[var(--mustard)] bg-[var(--mustard-dim)] border-[var(--mustard)]',
  proposal_drafted:       'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]',
  in_local_deliberation:  'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]',
  gathering_input:        'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]',
  opened:                 'text-[var(--text-sec)] bg-[var(--surface)] border-[var(--rule)]',
  sent:                   'text-[var(--text-sec)] bg-[var(--surface)] border-[var(--rule)]',
  draft:                  'text-[var(--text-ter)] bg-[var(--surface)] border-[var(--rule)]',
  closed:                 'text-[var(--text-ter)] bg-[var(--surface)] border-[var(--rule)]',
};

// ─── Next-action hint per ball status ────────────────────────────────────────

function BallNextAction({ status }: { status: CatchballBall['ballStatus'] }) {
  switch (status) {
    case 'sent':
    case 'opened':
      return <p className="text-xs text-[var(--text-ter)] mt-1.5">Awaiting branch acknowledgment</p>;
    case 'gathering_input':
      return <p className="text-xs text-[var(--text-ter)] mt-1.5">Gathering contributor input</p>;
    case 'in_local_deliberation':
      return <p className="text-xs text-[var(--text-ter)] mt-1.5">In local deliberation</p>;
    case 'proposal_drafted':
      return <p className="text-xs text-[var(--text-ter)] mt-1.5">Proposal drafted — awaiting submission</p>;
    case 'under_review':
      return <p className="text-xs text-[var(--text-ter)] mt-1.5">Under executive review</p>;
    case 'returned_for_revision':
      return <p className="text-xs text-[var(--mustard)] mt-1.5">Revision requested — branch responding</p>;
    default:
      return null;
  }
}

// ─── Ball Card ────────────────────────────────────────────────────────────────

function BallCard({
  ball,
  cycleId,
  onSelect,
}: {
  ball: CatchballBall;
  cycleId: string;
  onSelect?: (ballId: string, loopId: string) => void;
}) {
  const getTrack   = useTrackStore(state => state.getTrack);
  const track      = ball.trackId ? getTrack(ball.trackId) : undefined;
  const dir        = ballDirection(ball.ballStatus);
  const hasProposal = ballHasReturnedProposal(ball);
  const isDraft    = ball.ballStatus === 'draft';
  const statusCls  = BALL_STATUS_COLORS[ball.ballStatus] ?? 'text-[var(--text-ter)] bg-[var(--surface)] border-[var(--rule)]';

  const userId = useUserStore((s) => s.userId);
  const siblingBalls = useBallsForLoop(ball.loopId);
  const fetchBallsForLoop = useCatchballBallStore((s) => s.fetchBallsForLoop);
  const hasChildren = siblingBalls.some((b) => b.parentBallId === ball.id);
  // Same "I am the sender, state untouched" gate as BallStrip's Recall — see
  // that component for the full precondition list the server (recall_ball,
  // migration 738) still enforces.
  const canRecall =
    !!userId &&
    (ball.sentByUserId === userId || ball.createdBy === userId) &&
    ball.ballStatus === 'sent' &&
    !ball.openedAt &&
    !hasProposal &&
    !hasChildren;

  const DirIcon =
    dir === 'upward'   ? ArrowUp   :
    dir === 'returned' ? RotateCcw :
    dir === 'closed'   ? CheckCircle2 :
                         ArrowDown;

  const keyLabel = ball.proposalSubmittedAt
    ? `Proposal · ${new Date(ball.proposalSubmittedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
    : ball.sentAt
    ? `Sent · ${new Date(ball.sentAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
    : null;

  const cardClass = isDraft
    ? 'group flex flex-col  border border-dashed border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:border-[var(--slate)] hover:shadow-md transition-all overflow-hidden text-left w-full opacity-80 hover:opacity-100'
    : 'group flex flex-col  border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:border-[var(--slate)] dark:hover:border-[var(--slate)] hover:shadow-md transition-all overflow-hidden text-left w-full';

  const inner = (
    <>
      {/* Status band */}
      <div className={`flex items-center gap-1.5 px-4 py-2.5 border-b overflow-hidden ${statusCls} text-xs font-semibold`}>
        <DirIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{BALL_STATUS_LABELS[ball.ballStatus]}</span>
        {hasProposal && ball.ballStatus === 'proposal_returned' && (
          <span className="ml-auto shrink-0 flex items-center gap-1 font-medium opacity-75">
            <MessageSquare className="h-3 w-3" />
            Proposal in
          </span>
        )}
      </div>

      {/* Body */}
      <div className="px-4 pt-3 pb-4 flex-1">
        {(() => {
          const primaryLabel = ball.recipientOrgUnitName ?? ball.recipientUserName ?? null;
          return (
            <>
              <p className="text-sm font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-ter)] transition-colors leading-snug">
                {primaryLabel ?? ball.name}
              </p>
              {primaryLabel && (
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-0.5">
                  {ball.name}
                </p>
              )}
            </>
          );
        })()}

        {/* Track link — the key binding between ball and ranking context */}
        {track && (
          <div className="mt-2 flex items-center gap-1 text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] font-medium">
            <GitBranch className="h-3 w-3 shrink-0" />
            <span className="truncate">{track.name}</span>
          </div>
        )}
        {!track && ball.trackId && (
          <div className="mt-2 flex items-center gap-1 text-[10px] text-[var(--mustard)] dark:text-[var(--mustard)] font-medium">
            <GitBranch className="h-3 w-3 shrink-0" />
            <span>No track assigned</span>
          </div>
        )}

        {keyLabel && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-2">{keyLabel}</p>
        )}
        {ball.dueDate && dir === 'downward' && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-1">
            Due {formatCalendarDate(ball.dueDate, { month: 'short', day: 'numeric' })}
          </p>
        )}
        <BallNextAction status={ball.ballStatus} />
        {canRecall && (
          <div className="mt-2">
            <CatchballUndoAction
              triggerLabel="Recall"
              modalTitle="Recall this ball"
              description={`Nobody at ${ball.recipientOrgUnitName ?? ball.recipientUserName ?? 'the recipient'} has opened or claimed it yet — recalling puts it back in your drafts.`}
              confirmLabel="Recall"
              reasonPlaceholder="Why recall this ball? (required)"
              onConfirm={(reason) => recallBall(ball.id, reason)}
              onSuccess={() => void fetchBallsForLoop(ball.loopId)}
              successTitle="Ball recalled"
              successMessage="It is back in your drafts."
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-[var(--rule)] dark:border-[var(--rule)] flex items-center justify-between bg-[var(--surface)] dark:bg-[var(--surface)]">
        <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] font-medium">
          {isDraft ? CATCHBALL_LABELS.sendTheBall : 'Open Catchball'}
        </span>
        <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-dim)] group-hover:text-[var(--text-ter)] transition-colors" />
      </div>
    </>
  );

  if (onSelect) {
    return (
      <button type="button" onClick={() => onSelect(ball.id, ball.loopId)} className={cardClass}>
        {inner}
      </button>
    );
  }

  return (
    <Link
      to={`/prioritization/cascade/catchball/${cycleId}/${ball.loopId}?ballId=${encodeURIComponent(ball.id)}`}
      className={cardClass}
    >
      {inner}
    </Link>
  );
}

// ─── Exchange Path Section ────────────────────────────────────────────────────
// The "situation room" layout: Request origin → Ball grid → Reconciliation endpoint.
// Replaces the generic active-loop card with a directional, branch-aware view.

function ExchangePathSection({
  loop,
  balls,
  cycleId,
  iAmHolder,
  ballUnclaimed,
  canClaimBall,
  holderName,
  holderTierLabel,
  possessionBusy,
  onGrabBall,
  onReleaseBall,
  onTossToBottom,
  onReturnToMiddle,
  onReturnToTop,
  onAcceptOutput,
  onRequestAnotherRound,
  anotherRoundBlockedReason,
  onCloseLoop,
  packetProposal,
  onBallSelect,
  onOpenWorkspace,
}: {
  loop: CatchballLoop;
  balls: CatchballBall[];
  cycleId: string;
  /** Possession facts from computeLoopAccess — transition actions are holder-only. */
  iAmHolder: boolean;
  ballUnclaimed: boolean;
  canClaimBall: boolean;
  /** Resolved name of the current holder, null when unclaimed. */
  holderName: string | null;
  /** Org/tier label for the tier that owns the ball (unclaimed messaging). */
  holderTierLabel: string | null;
  possessionBusy: boolean;
  onGrabBall: (id: string) => void;
  onReleaseBall: (id: string) => void;
  onTossToMiddle: (id: string) => void;
  onTossToBottom: (id: string) => void;
  onReturnToMiddle: (id: string) => void;
  onReturnToTop: (id: string) => void;
  onAcceptOutput: (id: string) => void;
  onRequestAnotherRound: (id: string) => void;
  /** Why the server would refuse another round (763 gates), or null. */
  anotherRoundBlockedReason?: string | null;
  onCloseLoop: (id: string) => void;
  packetProposal?: { id: string; status?: string; itemTitle?: string; proposedAt?: string | null } | null;
  packetStatusLabel: (s: string | undefined) => string;
  onBallSelect?: (ballId: string, loopId: string) => void;
  onOpenWorkspace?: (loopId: string) => void;
}) {
  const loopLabel = useLoopDisplayLabel(loop);

  const returnedBalls  = balls.filter(b => PROPOSAL_RETURNED_STATUSES.includes(b.ballStatus));
  const allReturned    = balls.length > 0 && returnedBalls.length === balls.length;
  const someReturned   = returnedBalls.length > 0;

  const loopWorkspaceHref  = `/prioritization/cascade/workbench/${cycleId}/${loop.id}`;
  const reconciliationHref = `/prioritization/cascade/reconcile/${cycleId}`;

  const isClosed   = loop.state === 'closed' || loop.state === 'DECLINED';
  const isAccepted = loop.state === 'ACCEPTED' || loop.state === 'accepted_provisional' || loop.state === 'output_ready';

  const loopStatusChip = isClosed
    ? { label: 'Closed', cls: 'bg-[var(--slate)] text-white' }
    : isAccepted
    ? { label: 'Accepted', cls: 'bg-[var(--green)] text-white' }
    : allReturned
    ? { label: 'All proposals returned', cls: 'bg-[var(--slate)] text-white' }
    : someReturned
    ? { label: `${returnedBalls.length}/${balls.length} proposals returned`, cls: 'bg-[var(--slate)] text-white' }
    : balls.length > 0
    ? { label: 'Balls in flight', cls: 'bg-[var(--slate)] text-white' }
    : { label: 'Active', cls: 'bg-[var(--green)] text-white' };

  const requestText = loop.intent?.strategicContext
    ?? (loop.intent?.asks?.[0] ?? null);
  const constraintChips = normalizeStringList(loop.intent?.constraints, ';');

  const sentDate = balls.length > 0 && balls[0]?.sentAt
    ? new Date(balls[0].sentAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const reconcileStatus = allReturned
    ? `All ${balls.length} proposals returned — ready for reconciliation`
    : someReturned
    ? `${returnedBalls.length} of ${balls.length} proposals returned`
    : balls.length > 0
    ? 'Awaiting proposals from branches'
    : 'No Balls issued yet';

  return (
    <div className="overflow-hidden border border-[var(--rule)] shadow-sm">

      {/* ── Situation room header ─────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] px-5 py-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CircleDot className="h-4 w-4 text-[var(--green)] shrink-0" />
            <span className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
              {loopLabel}
            </span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${loopStatusChip.cls}`}>
              {loopStatusChip.label}
            </span>
          </div>
          <p className="text-base font-semibold text-white leading-snug">
            Catchball
          </p>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            {balls.length > 0
              ? `Request sent to ${balls.length} branch${balls.length !== 1 ? 'es' : ''} — each returns a Proposal`
              : 'Request composed — ready to send'}
          </p>
          {/* Ball possession — who personally holds the ball right now */}
          {!isClosed && (
            <p className="text-xs mt-1.5">
              {iAmHolder ? (
                <span className="inline-flex items-center gap-1 font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
                  <Hand className="h-3 w-3" />
                  Ball with you
                </span>
              ) : holderName ? (
                <span className="text-[var(--text-ter)]">
                  Ball with <span className="font-semibold text-[var(--text-ter)]">{holderName}</span>
                </span>
              ) : (
                <span className="text-[var(--mustard)]/90">
                  Ball unclaimed{holderTierLabel ? ` — with ${holderTierLabel}` : ''}
                </span>
              )}
            </p>
          )}
        </div>
        {onOpenWorkspace ? (
          <button
            type="button"
            onClick={() => onOpenWorkspace(loop.id)}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--text-ter)] hover:text-white transition-colors mt-0.5"
          >
            Open workspace
          </button>
        ) : (
          <Link
            to={loopWorkspaceHref}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--text-ter)] hover:text-white transition-colors mt-0.5"
          >
            Open workspace
            <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>

      {/* ── The Request ───────────────────────────────────────────────── */}
      <div className="bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border-b border-[var(--mustard)] dark:border-[var(--mustard)]/30 px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-[var(--mustard)] dark:text-[var(--mustard)] uppercase tracking-widest mb-1.5">
              The Request
            </p>
            {requestText ? (
              <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-sec)] line-clamp-3 leading-relaxed">
                {requestText}
              </p>
            ) : (
              <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-dim)] italic">
                No request text recorded.
              </p>
            )}
            {constraintChips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {constraintChips.slice(0, 3).map((c, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 text-[var(--mustard)] dark:text-[var(--mustard)] border border-[var(--mustard)] dark:border-[var(--mustard)]">
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
          {loop.intent?.dueDate && (
            <div className="shrink-0 text-right">
              <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wide">Due</p>
              <p className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                {formatCalendarDate(loop.intent.dueDate, { month: 'short', day: 'numeric' })}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Connector: request → balls ────────────────────────────────── */}
      {(() => {
        const draftBalls   = balls.filter(b => b.ballStatus === 'draft');
        const sentBalls    = balls.filter(b => b.ballStatus !== 'draft');
        const allDraft     = balls.length > 0 && draftBalls.length === balls.length;
        return (
          <div className="flex items-center justify-center gap-3 py-3 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border-b border-[var(--rule)] dark:border-[var(--rule)]">
            <div className="flex-1 h-px bg-[var(--hover-tint)] dark:bg-[var(--rule)] max-w-[6rem]" />
            <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">
              <ArrowDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              {sentBalls.length > 0
                ? <span>sent to <span className="font-semibold text-[var(--text-sec)] dark:text-[var(--text-ter)]">{sentBalls.length} branch{sentBalls.length !== 1 ? 'es' : ''}</span>{sentDate ? ` · ${sentDate}` : ''}</span>
                : allDraft
                ? <span className="text-[var(--mustard)] dark:text-[var(--mustard)] font-medium">{draftBalls.length} branch{draftBalls.length !== 1 ? 'es' : ''} ready to send</span>
                : <span className="italic">no Balls issued yet</span>
              }
            </div>
            <div className="flex-1 h-px bg-[var(--hover-tint)] dark:bg-[var(--rule)] max-w-[6rem]" />
          </div>
        );
      })()}

      {/* ── Ball grid ─────────────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-5 py-5">
        {balls.length === 0 ? (
          <div className="border border-dashed border-[var(--rule)] dark:border-[var(--rule)] px-5 py-8 text-center">
            <CircleDot className="h-6 w-6 text-[var(--text-ter)] dark:text-[var(--text-dim)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1">No Balls issued yet</p>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">
              {loop.state === 'drafting'
                ? `Previous round's branches will appear here. Click "${CATCHBALL_LABELS.sendTheBall}" to issue them.`
                : 'Toss the ball to create one Ball per recipient branch.'}
            </p>
          </div>
        ) : (
          <div className={`grid gap-3 ${
            balls.length === 1 ? 'grid-cols-1 max-w-xs' :
            balls.length === 2 ? 'grid-cols-2' :
            balls.length <= 4 ? 'grid-cols-2 sm:grid-cols-4' :
                                 'grid-cols-2 sm:grid-cols-3'
          }`}>
            {balls.map(ball => (
              <BallCard key={ball.id} ball={ball} cycleId={cycleId} onSelect={onBallSelect} />
            ))}
          </div>
        )}
      </div>

      {/* ── Connector: proposals returning upward ─────────────────────── */}
      {balls.length > 0 && (
        <div className="flex items-center justify-center gap-3 py-3 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border-t border-[var(--rule)] dark:border-[var(--rule)]">
          <div className="flex-1 h-px bg-[var(--hover-tint)] dark:bg-[var(--rule)] max-w-[6rem]" />
          <div className={`flex items-center gap-1.5 text-xs font-medium ${
            allReturned  ? 'text-[var(--green)] dark:text-[var(--text-ter)]' :
            someReturned ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' :
                           'text-[var(--text-ter)] dark:text-[var(--text-dim)]'
          }`}>
            <ArrowUp className="h-3.5 w-3.5" />
            <span>
              {allReturned
                ? `All ${balls.length} proposals returned`
                : someReturned
                ? `${returnedBalls.length} of ${balls.length} proposals returned`
                : `Waiting on ${balls.length} proposal${balls.length !== 1 ? 's' : ''}`}
            </span>
          </div>
          <div className="flex-1 h-px bg-[var(--hover-tint)] dark:bg-[var(--rule)] max-w-[6rem]" />
        </div>
      )}

      {/* ── Reconciliation endpoint ───────────────────────────────────── */}
      <div className={`px-5 py-4 border-t ${
        allReturned  ? 'bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border-[var(--green)] dark:border-[var(--green)]/30' :
        someReturned ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border-[var(--slate)] dark:border-[var(--slate)]/30' :
                       'bg-[var(--surface)] dark:bg-[var(--surface)] border-[var(--rule)] dark:border-[var(--rule)]'
      }`}>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <Scale className={`h-3.5 w-3.5 shrink-0 ${
                allReturned ? 'text-[var(--green)]' : someReturned ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'
              }`} />
              <p className={`text-[10px] font-bold uppercase tracking-widest ${
                allReturned ? 'text-[var(--green)] dark:text-[var(--text-ter)]' :
                someReturned ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' :
                               'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
              }`}>
                Reconciliation
              </p>
            </div>
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              {allReturned
                ? 'All proposals in — choose how to reconcile them into a single accepted output.'
                : reconcileStatus}
            </p>
          </div>
          {someReturned && (
            <Link
              to={reconciliationHref}
              className={`shrink-0 inline-flex items-center gap-1.5  px-3 py-1.5 text-sm font-semibold transition-colors ${
                allReturned
                  ? 'bg-[var(--green)] hover:bg-[var(--green)] text-white'
                  : 'bg-[var(--slate)] hover:bg-[var(--slate)] text-white'
              }`}
            >
              {allReturned ? 'Begin Reconciliation' : 'Review Proposals'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
          {!someReturned && !balls.length && packetProposal && (
            <Link
              to={`/prioritization/cascade/proposal/${packetProposal.id}`}
              className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
            >
              Review proposal
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>

      {/* ── Loop actions (three-state: hold → act, unclaimed → grab, else wait) ── */}
      {!isClosed && iAmHolder && (
        <div className="px-5 py-3 border-t border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] flex flex-wrap items-center gap-2">
          {(loop.state === 'OPEN' || loop.state === 'drafting') && (
            <Link
              to={`/prioritization/cascade/compose/${cycleId}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors"
            >
              <Send className="h-3 w-3" />
              {CATCHBALL_LABELS.sendTheBall}
            </Link>
          )}
          {/* A loop nobody has been asked anything in can be cancelled (mig 746);
              once a ball exists the server refuses and names it. */}
          {loop.state === 'drafting' && balls.length === 0 && (
            <ReverseActionButton
              label="Cancel loop"
              confirmTitle="Cancel this loop"
              explain="Use this when the loop was opened by mistake. Nothing has been sent, so it is removed and your reason is recorded in the cycle history."
              run={(reason) => cancelCatchballLoop(loop.id, reason)}
              onDone={() => void useCatchballLoopStore.getState().fetchFromSupabase(cycleId)}
              variant="ghost"
            />
          )}
          {/* Middle already has the ball — the only live next step is a toss
              down to the front line (catchball_toss_to_bottom requires
              state 'middle_working'; see migration 590/578). catchball_send_the_ball
              (744) can't express this move: it always mints new Balls for new
              recipients, and this step advances the SAME Balls/loop with none —
              so it stays on its own RPC, just behind the shared
              one-button-then-choose-level toss UI (owner ruling 2026-09-22, #14). */}
          {loop.state === 'middle_working' && (
            <CatchballTossButton
              levels={[
                { value: 'middle', label: 'Middle tier', disabledReason: 'Already with the middle tier' },
                { value: 'bottom', label: 'Front line' },
              ]}
              onConfirm={() => onTossToBottom(loop.id)}
            />
          )}
          {loop.state === 'tossed_to_bottom' && (
            <button
              type="button"
              onClick={() => onReturnToMiddle(loop.id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors"
            >
              <ArrowUpCircle className="h-3 w-3" />
              Return via middle
            </button>
          )}
          {loop.state === 'returned_to_middle' && (
            <button
              type="button"
              onClick={() => onReturnToTop(loop.id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors"
            >
              <ArrowUpCircle className="h-3 w-3" />
              Return to leadership
            </button>
          )}
          {loop.state === 'returned_to_top' && (
            <div className="flex flex-wrap items-center gap-2 w-full">
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] w-full">
                Reconciliation complete — accept the output, or close this round and open a new one.
              </p>
              <button
                type="button"
                onClick={() => onAcceptOutput(loop.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors"
              >
                <CheckCircle2 className="h-3 w-3" />
                Accept output
              </button>
              <button
                type="button"
                onClick={() => onRequestAnotherRound(loop.id)}
                disabled={!!anotherRoundBlockedReason}
                title={anotherRoundBlockedReason ?? 'Closes this loop and opens a new one on the same track. The closed loop stays in the history.'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Close this round and start another
              </button>
            </div>
          )}
          {(loop.state === 'ACCEPTED' || loop.state === 'output_ready') && (
            <button
              type="button"
              onClick={() => onCloseLoop(loop.id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--bg-raised)] text-white hover:bg-[var(--bg-raised)] transition-colors"
            >
              Close loop
            </button>
          )}
          {/* Low-emphasis relinquish — puts the ball back up for grabs */}
          <button
            type="button"
            disabled={possessionBusy}
            onClick={() => onReleaseBall(loop.id)}
            className="ml-auto text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-dim)] dark:hover:text-[var(--text-sec)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {CATCHBALL_LABELS.releaseTheBall}
          </button>
        </div>
      )}
      {!isClosed && !iAmHolder && ballUnclaimed && canClaimBall && (
        <div className="px-5 py-3 border-t border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={possessionBusy}
            onClick={() => onGrabBall(loop.id)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Hand className="h-3 w-3" />
            {CATCHBALL_LABELS.takeTheBall}
          </button>
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Take the ball to act on this loop at your tier.
          </span>
        </div>
      )}
      {!isClosed && !iAmHolder && !(ballUnclaimed && canClaimBall) && (
        <div className="px-5 py-3 border-t border-[var(--mustard)] dark:border-[var(--mustard)]/30 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
          {ballUnclaimed
            ? `The ball is unclaimed. Waiting for an eligible member of ${holderTierLabel ?? 'the holding tier'} to take it.`
            : `With ${holderName ?? 'another teammate'} — they must pass or release the ball before you can act.`}
        </div>
      )}
    </div>
  );
}

export function CatchballDashboard({
  embedded = false,
  overviewRail = false,
  cycleIdProp,
  onBallSelect,
  onOpenWorkspace,
  onOpenFullExchange,
}: {
  embedded?: boolean;
  overviewRail?: boolean;
  cycleIdProp?: string;
  onBallSelect?: (ballId: string, loopId: string) => void;
  onOpenWorkspace?: (loopId: string) => void;
  onOpenFullExchange?: () => void;
} = {}) {
  const { cycleId: cycleIdParam } = useParams();
  const cycleId = cycleIdProp ?? cycleIdParam;
  const location = useLocation();
  const navigate = useNavigate();
  const userId = useUserStore(state => state.userId);

  // Stores
  const createLoop = useCatchballLoopStore(state => state.createLoop);
  const tossToMiddle = useCatchballLoopStore(state => state.tossToMiddle);
  const tossToBottom = useCatchballLoopStore(state => state.tossToBottom);
  const returnToMiddle = useCatchballLoopStore(state => state.returnToMiddle);
  const returnToTop = useCatchballLoopStore(state => state.returnToTop);
  const acceptOutput = useCatchballLoopStore(state => state.acceptOutput);
  const requestAnotherRound = useCatchballLoopStore(state => state.requestAnotherRound);
  const closeLoop = useCatchballLoopStore(state => state.closeLoop);
  const claimBall = useCatchballLoopStore(state => state.claimBall);
  const releaseBall = useCatchballLoopStore(state => state.releaseBall);
  const fetchLoops = useCatchballLoopStore(state => state.fetchFromSupabase);
  const { showToast } = useToast();
  const { users: systemUsers } = useSystemUsers();
  const [possessionBusy, setPossessionBusy] = useState(false);

  const allLoops = useCatchballLoopStore(state => state.loops);
  const loops = useMemo(
    () => (cycleId ? allLoops.filter(l => l.cycleId === cycleId).sort((a, b) => a.loopNumber - b.loopNumber) : []),
    [allLoops, cycleId]
  );
  const cycles = useDegapProcessStore(state => state.cycles);
  const cycle = useMemo(() => cycles.find(c => c.id === cycleId), [cycles, cycleId]);
  const orgUnits = useEnterpriseIdentityStore(state => state.orgUnits);
  const userNodeIndex = useEnterpriseIdentityStore(state => state.userNodeIndex);
  const fetchEnterpriseIdentity = useEnterpriseIdentityStore(state => state.fetchFromSupabase);
  const fetchVirtualConfig = useCatchballVirtualConfigStore(state => state.fetchForCycle);
  const virtualConfig = useCatchballVirtualConfigStore(state => cycleId ? (state.configs[cycleId] ?? null) : null);
  const allBallsById = useCatchballBallStore(state => state.ballsById);
  const visibleLoops = useMemo(
    () =>
      loops.filter((loop) => {
        const ballsForLoop = Object.values(allBallsById).filter((b) => b.loopId === loop.id);
        return computeLoopVisibility(loop, userId ?? null, orgUnits, userNodeIndex, cycle?.orgScopeNodeId, virtualConfig, ballsForLoop, cycle?.ownerId ?? null).canSeeLoop;
      }),
    [loops, userId, orgUnits, userNodeIndex, cycle?.orgScopeNodeId, cycle?.ownerId, virtualConfig, allBallsById]
  );
  // A Cycle Designer launch creates ONE loop per track, so a four-path cycle holds
  // four loops side by side. Picking "the highest-numbered open loop" and offering no
  // way to change it left three of the four unreachable: the person a ball had just
  // been sent to opened Catchball and saw someone else's loop, with no control to
  // switch and no way to take their own ball. The loop the reader is looking at is now
  // their choice — from the picker, from ?trackId= (what the Cycle Designer links carry),
  // and only failing both from the old preference order.
  const [searchParams] = useSearchParams();
  const requestedTrackId = searchParams.get('trackId');
  const [pickedLoopId, setPickedLoopId] = useState<string | null>(null);
  const currentLoop = useMemo(
    () => {
      const picked = pickedLoopId ? visibleLoops.find(loop => loop.id === pickedLoopId) : undefined;
      if (picked) return picked;
      const byTrack = requestedTrackId
        ? visibleLoops.find(loop => loop.trackId === requestedTrackId && loop.state !== 'closed')
        : undefined;
      if (byTrack) return byTrack;
      // visibleLoops is sorted ascending by loopNumber — walk from the end so a later loop
      // (already in flight, or with Balls issued) is always preferred over an older,
      // abandoned non-closed loop (e.g. a stale duplicate left behind by a load-order race).
      return [...visibleLoops].reverse().find(loop => loop.state !== 'closed') || visibleLoops[visibleLoops.length - 1] || null;
    },
    [visibleLoops, pickedLoopId, requestedTrackId]
  );
  // Prefer the stable useBallsForLoop hook (EMPTY_BALLS when unloaded) — never call
  // getBallsForLoop during render (allocates a fresh array every time when balls exist).
  const currentLoopBalls = useBallsForLoop(currentLoop?.id);
  const branchBalls = useMemo(() => currentLoopBalls.filter((ball) => !isAnchorBall(ball)), [currentLoopBalls]);
  const currentLoopAccess = useMemo(
    () => computeLoopAccess(currentLoop, userId ?? null, orgUnits, userNodeIndex, cycle?.orgScopeNodeId, virtualConfig, currentLoopBalls, cycle?.ownerId ?? null),
    [currentLoop, userId, orgUnits, userNodeIndex, cycle?.orgScopeNodeId, cycle?.ownerId, virtualConfig, currentLoopBalls]
  );

  /** Name of the person holding the ball on the current loop, resolved via systemUsers. */
  const currentHolderName = useMemo(() => {
    const holderId = currentLoopAccess.currentHolderUserId;
    if (!holderId) return null;
    const u = systemUsers.find((x) => x.id === holderId);
    return u?.name || u?.email || 'the current holder';
  }, [currentLoopAccess.currentHolderUserId, systemUsers]);

  const getDefaultTrack = useTrackStore(state => state.getDefaultTrack);
  const fetchTracks = useTrackStore(state => state.fetchTracksForCycle);
  const tracks = useTrackStore(state => state.tracks);

  const fetchBallsForCycle = useCatchballBallStore(state => state.fetchBallsForCycle);

  const fetchProposals = useProposalPacketStore(state => state.fetchProposals);
  const fetchResponsibilities = useResponsibilityStore(state => state.fetchResponsibilities);
  const allPacketStoreProposals = useProposalPacketStore(state => state.proposals);
  const packetProposals = useMemo(
    () => (allPacketStoreProposals || []).filter(p => p.cycleId === cycleId && (p as { proposalType?: string }).proposalType === 'packet'),
    [allPacketStoreProposals, cycleId]
  );

  const fetchCatchballProposals = useCatchballStore(state => state.fetchFromSupabase);
  const allProposals = useCatchballStore(state => state.proposals);
  const itemLevelProposals = useMemo(
    () => Object.values(allProposals || {}).filter(p => p.cycleId === cycleId && (p as { proposalType?: string }).proposalType === 'item'),
    [allProposals, cycleId]
  );

  // ── Selector hooks — consume the data layer that was defined but never rendered ──
  const pendingActions = usePendingCatchballActions(userId ?? '', cycleId);
  const threads = useCatchballThreads(cycleId ?? '');

  const [directionFilter, setDirectionFilter] = useState<CatchballDirection | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const filteredItemProposals = itemLevelProposals.filter(p => {
    if (directionFilter !== 'all' && p.direction !== directionFilter) return false;
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    return true;
  });

  useEffect(() => {
    if (cycleId) {
      fetchProposals(cycleId);
      fetchLoops(cycleId);
      fetchTracks(cycleId);
      void fetchEnterpriseIdentity();
      void fetchCatchballProposals();
      void fetchVirtualConfig(cycleId);
      void fetchBallsForCycle(cycleId);
    }
    if (userId) {
      fetchResponsibilities(userId);
    }
  }, [cycleId, fetchEnterpriseIdentity, fetchProposals, fetchLoops, fetchTracks, userId, fetchResponsibilities, fetchCatchballProposals, fetchVirtualConfig, fetchBallsForCycle]);

  // Keep the flash banner visible for 4 s so the user can read it, then clear it.
  useEffect(() => {
    if (!(location.state && typeof location.state === 'object' && 'catchballFlash' in location.state)) return;
    const timer = setTimeout(() => {
      navigate(location.pathname, { replace: true, state: {} });
    }, 4000);
    return () => clearTimeout(timer);
  }, [location.pathname, location.state, navigate]);

  // ============================================================================
  // Catchball action handlers
  // ============================================================================

  const handleStartLoop = useCallback(async () => {
    if (!cycleId || !userId) return;
    let defaultTrack = getDefaultTrack(cycleId);
    if (!defaultTrack) {
      const scopedOrgUnit = cycle?.orgScopeNodeId ? orgUnits.get(cycle.orgScopeNodeId) ?? null : null;
      const created = await createTrackWithFeedback({
        cycleId,
        name: cycle?.name ?? 'Track 1',
        description: 'Default Track for this cycle',
        scopeType: scopedOrgUnit ? 'org_unit' : 'default',
        scopeEntityId: scopedOrgUnit?.id ?? undefined,
        scopeLabel: scopedOrgUnit?.displayName ?? 'Default',
      });
      if (!created.ok || !created.track) return;
      defaultTrack = created.track;
    }
    const createdLoop = await createLoop(cycleId, defaultTrack.id, userId);
    if (createdLoop) {
      navigate(`/prioritization/cascade/compose/${cycleId}`);
    }
  }, [cycleId, userId, getDefaultTrack, createLoop, navigate, cycle, orgUnits]);

  // Loop transitions return false / null (they never throw) when the server or
  // policy refuses; the reason is on the loop store's `error`. Clear it first
  // so a stale message from an earlier action is never shown.
  const runLoopAction = useCallback(
    async <T,>(failTitle: string, action: () => Promise<T>): Promise<T | null> => {
      useCatchballLoopStore.setState({ error: null });
      try {
        const result = await action();
        if (result === false || result === null || result === undefined) {
          const rpcError = useCatchballLoopStore.getState().error;
          showToast(
            'error',
            failTitle,
            rpcError ?? 'Nothing was changed. You may not have permission for this step — try again or ask the cycle owner.',
          );
          return null;
        }
        return result;
      } catch (err) {
        showToast('error', failTitle, err instanceof Error ? err.message : String(err));
        return null;
      }
    },
    [showToast],
  );

  const handleTossToMiddle = useCallback(async (loopId: string) => {
    if (!userId) return;
    const recipientOrgUnitIds = cycle?.orgScopeNodeId
      ? getWorkingTierAssignableOrgUnits(cycle.orgScopeNodeId, Array.from(orgUnits.values()))
          .filter((unit) => unit.id !== cycle.orgScopeNodeId)
          .map((unit) => unit.id)
      : undefined;
    const drivers = (cycle?.outputs?.decide?.outcomeDrivers as string[] | undefined)?.join(', ') ?? '';
    const ok = await runLoopAction('Request not sent', () => tossToMiddle(loopId, {
      strategicContext: drivers,
      asks: ['Prioritize items within your scope'],
      constraints: [],
      expectedResponseType: 'options',
      recipientOrgUnitIds,
    }, userId));
    if (ok) showToast('success', 'Request sent', 'The ball is with the next level.');
  }, [userId, cycle, orgUnits, tossToMiddle, runLoopAction, showToast]);

  const handleTossToBottom = useCallback(async (loopId: string) => {
    if (!userId) return;
    const ok = await runLoopAction('Not forwarded', () => tossToBottom(loopId, userId));
    if (ok) showToast('success', 'Forwarded', 'The ball moved down to the next level.');
  }, [userId, tossToBottom, runLoopAction, showToast]);

  const handleReturnToMiddle = useCallback(async (loopId: string) => {
    if (!userId) return;
    const ok = await runLoopAction('Not returned', () => returnToMiddle(loopId, userId));
    if (ok) showToast('success', 'Returned', 'The ball went back up one level.');
  }, [userId, returnToMiddle, runLoopAction, showToast]);

  const handleReturnToTop = useCallback(async (loopId: string, synthesis?: LoopSynthesis) => {
    if (!userId) return;
    const ok = await runLoopAction('Not returned to leadership', () => returnToTop(loopId, synthesis || {
      summary: '',
      tensions: [],
      recommendations: [],
      focusListIds: [],
      unresolvedItems: [],
      synthesizedBy: userId,
      synthesizedAt: new Date(),
    }, userId));
    if (ok) showToast('success', 'Returned to leadership', 'Leadership can now accept the output or ask for another round.');
  }, [userId, returnToTop, runLoopAction, showToast]);

  const handleAcceptOutput = useCallback(async (loopId: string) => {
    if (!userId) return;
    const ok = await runLoopAction('Output not accepted', () => acceptOutput(loopId, userId));
    if (ok) showToast('success', 'Output accepted', 'This round is accepted.');
  }, [userId, acceptOutput, runLoopAction, showToast]);

  // "Another round" closes THIS loop and opens a new drafting loop on the same
  // track (catchball_request_another_round). Take the user to the new loop.
  const handleRequestAnotherRound = useCallback(async (loopId: string) => {
    if (!userId || !cycleId) return;
    const trackId = loops.find((l) => l.id === loopId)?.trackId;
    const newLoopId = await runLoopAction('New round not started', () => requestAnotherRound(loopId, userId));
    if (!newLoopId) return;
    showToast('success', 'New round opened', 'The previous round is closed. Write the request for the new round.');
    navigate(
      trackId
        ? `/prioritization/cascade/compose/${cycleId}?trackId=${encodeURIComponent(trackId)}`
        : `/prioritization/cascade/compose/${cycleId}`,
    );
  }, [userId, cycleId, loops, requestAnotherRound, runLoopAction, showToast, navigate]);

  const handleCloseLoop = useCallback(async (loopId: string) => {
    if (!userId) return;
    const ok = await runLoopAction('Loop not closed', () => closeLoop(loopId, userId));
    if (ok) showToast('success', 'Loop closed', 'This loop is closed.');
  }, [userId, closeLoop, runLoopAction, showToast]);

  // Grab/release the ball — the claim/release RPCs are authoritative; on
  // failure surface their error.
  const handleGrabBall = useCallback(async (loopId: string) => {
    if (!userId) return;
    setPossessionBusy(true);
    try {
      const ok = await claimBall(loopId, userId);
      if (ok) {
        showToast('success', 'You have the ball', 'You are now the holder for this tier.');
      } else {
        const rpcError = useCatchballLoopStore.getState().error;
        showToast('error', 'Could not take the ball', rpcError ?? 'Failed to claim the ball');
      }
    } finally { setPossessionBusy(false); }
  }, [userId, claimBall, showToast]);

  const handleReleaseBall = useCallback(async (loopId: string) => {
    if (!userId) return;
    setPossessionBusy(true);
    try {
      const ok = await releaseBall(loopId, userId);
      if (ok) {
        showToast('success', 'Ball released', 'The ball is unclaimed — an eligible teammate can take it.');
      } else {
        const rpcError = useCatchballLoopStore.getState().error;
        showToast('error', 'Could not release the ball', rpcError ?? 'Failed to release the ball');
      }
    } finally { setPossessionBusy(false); }
  }, [userId, releaseBall, showToast]);

  // ============================================================================
  // Status helpers
  // ============================================================================

  const packetStatusLabel = (status: string | undefined) => {
    switch (status) {
      case 'pending': return 'Awaiting executive response';
      case 'accepted': return 'Accepted';
      case 'rejected': return 'Rejected';
      case 'countered': return 'Returned for revision';
      default:
        if (status && /revision|returned/i.test(String(status))) return 'Returned for revision';
        return status ?? 'No status';
    }
  };

  // ── Local state for loop history accordion ──────────────────────────────
  const [historyOpen, setHistoryOpen] = useState(false);
  const [negotiationsOpen, setNegotiationsOpen] = useState(false);
  const [threadsOpen, setThreadsOpen] = useState(false);

  // ── Active loop helpers ──────────────────────────────────────────────────
  const activeLoop: CatchballLoop | null = currentLoop;
  const closedLoops = visibleLoops.filter(l => l.state === 'closed' || l.state === 'DECLINED');
  const pendingNegotiations = itemLevelProposals.filter(p => p.status === 'pending');

  // ── Cycle Overview right rail: loops + exchange path only (no history accordion) ──
  if (overviewRail && cycleId) {
    return (
      <div className="space-y-4">
        {loops.length > 0 && visibleLoops.length === 0 && (
          <div className="border border-[var(--mustard)] dark:border-[var(--mustard)]/50 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 px-3 py-2 text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
            No loops are visible in your org scope.
          </div>
        )}
        {pendingActions.length > 0 && (
          <div className="border border-[var(--mustard)] dark:border-[var(--mustard)]/40 bg-[var(--mustard-dim)]/90 dark:bg-[var(--mustard)]/25 px-3 py-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
              <strong>{pendingActions.length}</strong> proposal{pendingActions.length !== 1 ? 's' : ''} need your response
            </span>
            {onOpenFullExchange && (
              <button
                type="button"
                onClick={onOpenFullExchange}
                className="text-xs font-semibold text-[var(--mustard)] dark:text-[var(--mustard)] hover:underline shrink-0"
              >
                Respond in Catchball →
              </button>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            <span className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{visibleLoops.length}</span>
            {' '}
            alignment loop{visibleLoops.length !== 1 ? 's' : ''}
            {currentLoop ? (
              <>
                {' '}
                · Ball on loop <span className="font-medium">{currentLoop.loopNumber}</span>
              </>
            ) : null}
          </p>
          {onOpenFullExchange && (
            <button
              type="button"
              onClick={onOpenFullExchange}
              className="text-xs font-semibold text-[var(--green)] dark:text-[var(--text-ter)] hover:underline shrink-0"
            >
              Open Catchball →
            </button>
          )}
        </div>
        {activeLoop ? (
          <ExchangePathSection
            loop={activeLoop}
            balls={branchBalls}
            cycleId={cycleId}
            iAmHolder={currentLoopAccess.iAmHolder}
            ballUnclaimed={currentLoopAccess.ballUnclaimed}
            canClaimBall={currentLoopAccess.canClaimBall}
            holderName={currentHolderName}
            holderTierLabel={currentLoopAccess.currentHolderLabel}
            possessionBusy={possessionBusy}
            onGrabBall={handleGrabBall}
            onReleaseBall={handleReleaseBall}
            onTossToMiddle={handleTossToMiddle}
            onTossToBottom={handleTossToBottom}
            onReturnToMiddle={handleReturnToMiddle}
            onReturnToTop={handleReturnToTop}
            onAcceptOutput={handleAcceptOutput}
            onRequestAnotherRound={handleRequestAnotherRound}
            anotherRoundBlockedReason={anotherRoundBlocker(activeLoop, loops, cycle, userId)}
            onCloseLoop={handleCloseLoop}
            packetProposal={packetProposals.find((p) => p.loopId === activeLoop.id)}
            packetStatusLabel={packetStatusLabel}
            onBallSelect={onBallSelect}
            onOpenWorkspace={onOpenWorkspace}
          />
        ) : (
          <div className="border border-dashed border-[var(--green)] dark:border-[var(--green)]/50 bg-[var(--green-dim)]/40 dark:bg-[var(--green)]/15 px-4 py-6 text-center">
            <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">No active alignment loop.</p>
            {onOpenFullExchange && (
              <button
                type="button"
                onClick={onOpenFullExchange}
                className="text-xs font-semibold text-[var(--green)] dark:text-[var(--text-ter)] hover:underline"
              >
                Open Catchball to start one →
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-5' : 'min-h-full bg-[var(--surface)] dark:bg-[var(--bg)]'}>
      {!embedded && cycleId && <CatchballStickyHeader cycleId={cycleId} active="dashboard" />}
      <div className={embedded ? 'space-y-5' : 'space-y-5 p-6'}>
      {!embedded && (
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          icon={<CircleDot className="h-4 w-4 text-[var(--green)]" />}
          iconBg="bg-[var(--green-dim)]"
          title="Catchball"
          subtitle={cycle ? `${cycle.name} · Organizational exchange` : 'Authority, intent, and judgment moving through the organization'}
        />
        {cycleId && (
          <Link
            to={`/prioritization/cascade/compose/${cycleId}`}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-[var(--green)] hover:bg-[var(--green)] text-white transition-colors mt-1"
          >
            <Send className="h-3.5 w-3.5" />
            New loop
          </Link>
        )}
      </div>
      )}

      {/* ── Flash message ───────────────────────────────────────────────── */}
      {location.state && typeof location.state === 'object' && 'catchballFlash' in location.state && (
        <div
          className={
            (location.state as { catchballFlash?: { tone?: string } }).catchballFlash?.tone === 'warning'
              ? 'border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3 text-sm text-[var(--text-pri)]'
              : 'border border-[var(--green)] dark:border-[var(--green)]/50 bg-[var(--green-dim)] dark:bg-[var(--green)]/20 px-4 py-3 text-sm text-[var(--green)] dark:text-[var(--text-ter)]'
          }
        >
          {(location.state as { catchballFlash?: { message?: string } }).catchballFlash?.message}
        </div>
      )}

      {/* ── Out-of-scope warning ────────────────────────────────────────── */}
      {cycleId && loops.length > 0 && visibleLoops.length === 0 && (
        <div className="border border-[var(--mustard)] dark:border-[var(--mustard)]/50 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 px-4 py-4 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
          No loops are within your org scope. Contact your administrator if you believe this is incorrect.
        </div>
      )}


      {/* ── Pending action queue (proposals targeting the current user) ────── */}
      {cycleId && pendingActions.length > 0 && (
        <div className="border border-[var(--mustard)] dark:border-[var(--mustard)]/50 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border-b border-[var(--mustard)] dark:border-[var(--mustard)]/50">
            <div className="flex items-center gap-2.5">
              <AlertCircle className="h-4 w-4 text-[var(--mustard)] dark:text-[var(--mustard)]" />
              <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Needs your response</span>
              <span className="px-2 py-0.5 rounded-full bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40 text-[var(--mustard)] dark:text-[var(--mustard)] text-xs font-semibold">
                {pendingActions.length}
              </span>
            </div>
            {pendingActions.some(a => a.isOverdue) && (
              <span className="text-xs font-medium text-[var(--coral)] dark:text-[var(--coral)]">
                {pendingActions.filter(a => a.isOverdue).length} overdue
              </span>
            )}
          </div>
          <ul className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {pendingActions.map(action => (
              <li key={action.proposalId} className="px-5 py-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                        {action.itemTitle || 'Item proposal'}
                      </span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        action.direction === 'down'
                          ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30'
                          : 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/30 dark:text-[var(--text-ter)]'
                      }`}>
                        {action.direction === 'down' ? '↓ From leadership' : '↑ From team'}
                      </span>
                      {action.isOverdue && (
                        <span className="text-xs font-medium text-[var(--coral)] dark:text-[var(--coral)]">Overdue</span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                      From {action.proposedByName || action.proposedBy} · {action.daysPending}d pending
                      {action.proposal.action && ` · ${action.proposal.action === 'include' ? 'Include' : action.proposal.action === 'exclude' ? 'Exclude' : 'Re-rank'}`}
                      {action.proposal.rank != null && ` to rank #${action.proposal.rank}`}
                    </p>
                    {action.rationale && (
                      <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] line-clamp-1">{action.rationale}</p>
                    )}
                  </div>
                  {/* The decision is made on the proposal's review page, through
                      catchball_accept / catchball_reject (one accept with options:
                      plain, with conditions, or provisional). The list only holds
                      proposals naming this person as a recipient, whom the server
                      allows to decide. DEP-023 retired the in-memory buttons. */}
                  <Link
                    to={`/prioritization/cascade/proposal/${action.proposalId}`}
                    className="shrink-0 px-2.5 py-1.5 text-xs font-medium bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors"
                  >
                    Review
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ══ WHICH LOOP ══════════════════════════════════════════════════════ */}
      {visibleLoops.length > 1 && (
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            This cycle has {visibleLoops.length} loops — one per track
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {visibleLoops.map(loop => {
              const isCurrent = loop.id === currentLoop?.id;
              const waitingOnMe =
                loop.state !== 'closed' &&
                !!userId &&
                (loop.ballHolderUserId === userId || !loop.ballHolderUserId);
              return (
                <button
                  key={loop.id}
                  type="button"
                  onClick={() => setPickedLoopId(loop.id)}
                  aria-current={isCurrent ? 'true' : undefined}
                  className={`inline-flex items-center gap-2 border px-3 py-1.5 text-sm transition-colors ${
                    isCurrent
                      ? 'border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] font-medium'
                      : 'border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
                  }`}
                >
                  {loopDisplayLabel(loop, loops, tracks)}
                  {loop.state === 'closed' ? (
                    <span className="text-xs text-[var(--text-ter)]">closed</span>
                  ) : waitingOnMe && !isCurrent ? (
                    <span className="text-xs text-[var(--mustard)]">your turn</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ══ EXCHANGE PATH ═══════════════════════════════════════════════════ */}
      {cycleId && (
        activeLoop ? (
          <ExchangePathSection
            loop={activeLoop}
            balls={branchBalls}
            cycleId={cycleId}
            iAmHolder={currentLoopAccess.iAmHolder}
            ballUnclaimed={currentLoopAccess.ballUnclaimed}
            canClaimBall={currentLoopAccess.canClaimBall}
            holderName={currentHolderName}
            holderTierLabel={currentLoopAccess.currentHolderLabel}
            possessionBusy={possessionBusy}
            onGrabBall={handleGrabBall}
            onReleaseBall={handleReleaseBall}
            onTossToMiddle={handleTossToMiddle}
            onTossToBottom={handleTossToBottom}
            onReturnToMiddle={handleReturnToMiddle}
            onReturnToTop={handleReturnToTop}
            onAcceptOutput={handleAcceptOutput}
            onRequestAnotherRound={handleRequestAnotherRound}
            anotherRoundBlockedReason={anotherRoundBlocker(activeLoop, loops, cycle, userId)}
            onCloseLoop={handleCloseLoop}
            packetProposal={packetProposals.find(p => p.loopId === activeLoop.id)}
            packetStatusLabel={packetStatusLabel}
            onBallSelect={onBallSelect}
            onOpenWorkspace={onOpenWorkspace}
          />
        ) : (
          /* No active loop — first-run or between loops */
          <div className="border border-dashed border-[var(--green)] dark:border-[var(--green)]/50 bg-gradient-to-br from-teal-50/50 to-white dark:from-teal-950/20 dark:to-[var(--bg-raised)] p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-[var(--green-dim)] dark:bg-[var(--green)]/40 flex items-center justify-center mx-auto mb-3">
              <Send className="h-5 w-5 text-[var(--green)] dark:text-[var(--text-ter)]" />
            </div>
            <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">No active loop</h3>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4 max-w-xs mx-auto">
              Start a Cascade Loop to send requests to your organizational branches and gather Proposals.
            </p>
            {cycleId && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                <Link
                  to={`/prioritization/cascade/compose/${cycleId}`}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors"
                >
                  <Send className="h-3.5 w-3.5" />
                  Start a loop
                </Link>
                {currentLoopAccess.canActOnLoop && (
                  <button
                    type="button"
                    onClick={handleStartLoop}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    Quick start
                  </button>
                )}
              </div>
            )}
          </div>
        )
      )}

      {/* ══ PROPOSAL EXPLORER — removed: pre-Ball item-level proposals superseded by Ball/Reconciliation model */}
      {SHOW_LEGACY_ITEM_PROPOSAL_PANELS && itemLevelProposals.length > 0 && (
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden">
          <button
            type="button"
            onClick={() => setNegotiationsOpen(v => !v)}
            className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <MessageSquare className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
              <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Item proposals</span>
              <span className="px-2 py-0.5 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-ter)] text-xs font-medium">
                {itemLevelProposals.length}
              </span>
              {pendingNegotiations.length > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/30 dark:text-[var(--mustard)] text-xs font-semibold">
                  {pendingNegotiations.length} pending
                </span>
              )}
            </div>
            {negotiationsOpen
              ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
              : <ChevronDown className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />}
          </button>
          {negotiationsOpen && (
            <div>
              <div className="flex items-center gap-3 px-5 py-2.5 border-t border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)]">
                <select
                  value={directionFilter}
                  onChange={e => setDirectionFilter(e.target.value as CatchballDirection | 'all')}
                  className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] rounded border border-[var(--rule)] dark:border-[var(--rule-bright)] text-xs px-2 py-1"
                >
                  <option value="all">All directions</option>
                  <option value="down">Downward</option>
                  <option value="up">Upward</option>
                </select>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] rounded border border-[var(--rule)] dark:border-[var(--rule-bright)] text-xs px-2 py-1"
                >
                  <option value="all">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="countered">Countered</option>
                  <option value="rejected">Rejected</option>
                  <option value="withdrawn">Withdrawn</option>
                </select>
                <span className="ml-auto text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                  {filteredItemProposals.length} result{filteredItemProposals.length !== 1 ? 's' : ''}
                </span>
              </div>
              <ul className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {filteredItemProposals.slice(0, 30).map(proposal => (
                  <li key={proposal.id} className="px-5 py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{proposal.itemTitle || proposal.itemId || 'Item'}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          proposal.direction === 'down'
                            ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30'
                            : 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/30 dark:text-[var(--text-ter)]'
                        }`}>
                          {proposal.direction === 'down' ? '↓' : '↑'}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                        {formatProposalAction(proposal)} · {proposal.proposedByName || proposal.proposedBy}
                        {proposal.proposedAt && ` · ${new Date(proposal.proposedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                      </p>
                      {proposal.rationale && (
                        <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] line-clamp-1">{proposal.rationale}</p>
                      )}
                    </div>
                    <span className={`shrink-0 inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                      proposal.status === 'pending' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/30 dark:text-[var(--mustard)]' :
                      proposal.status === 'accepted' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/30 dark:text-[var(--text-ter)]' :
                      proposal.status === 'countered' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30' :
                      proposal.status === 'rejected' ? 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/30 dark:text-[var(--coral)]' :
                      'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:text-[var(--text-ter)]'
                    }`}>
                      {getProposalStatusLabel(proposal.status)}
                    </span>
                  </li>
                ))}
              </ul>
              {filteredItemProposals.length > 30 && (
                <p className="px-5 py-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] border-t border-[var(--rule)] dark:border-[var(--rule)]">
                  Showing 30 of {filteredItemProposals.length}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══ ITEM-LEVEL PROPOSAL EXCHANGE — removed: pre-Ball negotiation threads superseded by Ball/Reconciliation model */}
      {SHOW_LEGACY_ITEM_PROPOSAL_PANELS && threads.length > 0 && (
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden">
          <button
            type="button"
            onClick={() => setThreadsOpen(v => !v)}
            className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <GitBranch className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
              <span className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">Item-level proposal exchange</span>
              {threads.filter(t => t.status === 'active').length > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30 text-xs font-medium">
                  {threads.filter(t => t.status === 'active').length} active
                </span>
              )}
              {threads.filter(t => t.status === 'stalled').length > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/30 dark:text-[var(--mustard)] text-xs font-medium">
                  {threads.filter(t => t.status === 'stalled').length} stalled
                </span>
              )}
            </div>
            {threadsOpen
              ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
              : <ChevronDown className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />}
          </button>
          {threadsOpen && (
            <ul className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)] border-t border-[var(--rule)] dark:border-[var(--rule)]">
              {threads
                .slice()
                .sort((a, b) => {
                  const order = { active: 0, stalled: 1, resolved: 2 };
                  return (order[a.status] - order[b.status]) || new Date(b.latestProposalAt).getTime() - new Date(a.latestProposalAt).getTime();
                })
                .map(thread => (
                  <li key={thread.id} className="px-5 py-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{thread.itemTitle}</p>
                          <span className={`shrink-0 inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                            thread.status === 'active' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30' :
                            thread.status === 'stalled' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/30 dark:text-[var(--mustard)]' :
                            'bg-[var(--hover-tint)] text-[var(--text-ter)] dark:bg-[var(--surface)] dark:text-[var(--text-ter)]'
                          }`}>
                            {thread.status}
                          </span>
                        </div>
                        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                          {thread.proposalCount} proposal{thread.proposalCount !== 1 ? 's' : ''} · {thread.participants.length} participant{thread.participants.length !== 1 ? 's' : ''}
                          {' · '}last activity {new Date(thread.latestProposalAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </p>
                        {thread.status === 'resolved' && thread.resolution && (
                          <p className="mt-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
                            Resolved {thread.resolution.finalRank != null ? `at rank #${thread.resolution.finalRank}` : ''}
                            {thread.resolution.finalCutLine ? ` · ${thread.resolution.finalCutLine} the line` : ''}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                        <MessageSquare className="h-3 w-3" />
                        {thread.proposalCount}
                      </div>
                    </div>
                  </li>
                ))
              }
            </ul>
          )}
        </div>
      )}

      {/* ══ LOOP HISTORY ══════════════════════════════════════════════════ */}
      {closedLoops.length > 0 && (
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden">
          <button
            type="button"
            onClick={() => setHistoryOpen(v => !v)}
            className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <Clock className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
              <span className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">Loop history</span>
              <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">{closedLoops.length} closed</span>
            </div>
            {historyOpen
              ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
              : <ChevronDown className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />}
          </button>
          {historyOpen && (
            <ul className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)] border-t border-[var(--rule)] dark:border-[var(--rule)]">
              {closedLoops.map(loop => {
                const packet = packetProposals.find(p => p.loopId === loop.id);
                const loopBalls = Object.values(allBallsById).filter((b) => b.loopId === loop.id);
                const workspaceHref = `/prioritization/cascade/workbench/${cycleId}/${loop.id}`;
                return (
                  <li key={loop.id} className="px-5 py-3 hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] transition-colors">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <Link to={workspaceHref} className="flex-1 min-w-0 group">
                        <p className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-ter)] transition-colors">{loopDisplayLabel(loop, allLoops, tracks)}</p>
                        {packet && !loopBalls.length && (
                          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-0.5">
                            {packetStatusLabel(packet.status)}
                            {packet.proposedAt && ` · ${new Date(packet.proposedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
                          </p>
                        )}
                      </Link>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="px-2 py-0.5 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface)] text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs">Closed</span>
                        <Link
                          to={workspaceHref}
                          className="inline-flex items-center gap-1 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
                        >
                          Open loop →
                        </Link>
                        {loopBalls.length > 0 && cycleId && (
                          <Link
                            to={`/prioritization/cascade/reconcile/${cycleId}`}
                            className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] transition-colors"
                          >
                            View reconciliation
                          </Link>
                        )}
                        {!loopBalls.length && packet && (
                          <Link
                            to={`/prioritization/cascade/proposal/${packet.id}`}
                            className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors"
                          >
                            View proposal
                          </Link>
                        )}
                      </div>
                    </div>
                    {loopBalls.length > 0 && (
                      <BallStrip
                        loopId={loop.id}
                        cycleId={cycleId ?? ''}
                        compact
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

export default CatchballDashboard;
